import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Paper,
  Chip,
  TextField,
  Tooltip,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Popover,
} from '@mui/material';
import {
  Route as RouteIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Share as ShareIcon,
  InfoOutlined as InfoOutlinedIcon,
  FactCheck as FactCheckIcon,
  CompareArrows as CompareArrowsIcon,
  ArrowBack as ArrowBackIcon,
  Undo as UndoIcon,
  Terrain as TerrainIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { attentionPulse, pulseAnimation, PULSE_TOTAL_MS } from '../../utils/attentionPulse';
import { useWorkoutSession } from '../../contexts/WorkoutSessionContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { uploadRunningFit, predictRunningRace, predictDemocratic, downloadRunningRaceGpx, getRunningPrediction, buildRacePlan, buildPredictionRacePlan, getRunningProfile, createAccuracyReport } from '../../api/runningApi';
import { getMyClubs, shareClubPrediction, getClubFileBlob } from '../../api/clubApi';
import RunningAnalysisView from '../../components/running/RunningAnalysisView';
import RacePredictionHelp from '../../components/running/RacePredictionHelp';
import ComparisonSelectorDrawer from '../../components/running/ComparisonSelectorDrawer';
import WorkoutPickerDrawer from '../../components/running/WorkoutPickerDrawer';
import ShareWorkoutDialog from '../../components/running/ShareWorkoutDialog';
import CourseSelectorDrawer from '../../components/running/CourseSelectorDrawer';
import RaceDetailDialog from '../../components/running/RaceDetailDialog';
import GarminAttribution from '../../components/garmin/GarminAttribution';
import LiveTrackingPanel from '../../components/running/live/LiveTrackingPanel';
import { buildPredictionWorkoutData } from '../../utils/runningPrediction';
import { apiErrorMessage } from '../../utils/apiError';

// ── Stop time helpers ─────────────────────────────────────────────────────────
const secsToHHMM = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
};
const hhmmToSecs = (str) => {
  if (!str) return 0;
  const parts = str.split(':');
  if (parts.length === 2) {
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 3600 + m * 60;
  }
  return (parseInt(str, 10) || 0) * 60;
};
const isValidHHMM = (str) => /^\d+:\d{2}$/.test(str?.trim() || '');

const RunningAnalysis = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const pulse = attentionPulse(colors);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setActiveSessionData } = useWorkoutSession();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  // Workout data from navigation state
  const [workoutData] = useState(location.state?.workoutData || null);
  const [file] = useState(location.state?.file || null);
  const [reusedFileId] = useState(location.state?.reusedFileId || null);
  const [analysisParams] = useState(location.state?.analysisParams || null);
  // Club context — set when navigating from a club file
  const [clubContext] = useState(location.state?.clubContext || null);

  // Prediction loaded directly from dashboard (no workoutData)
  const navPrediction = location.state?.predictionData || null;

  // What's currently displayed — actual or predicted
  const [activeWorkoutData, setActiveWorkoutData] = useState(
    navPrediction ? buildPredictionWorkoutData(navPrediction, navPrediction.race || null, navPrediction.race?.race_start || navPrediction.race_start) : workoutData
  );
  const [isPrediction, setIsPrediction] = useState(!!navPrediction);
  const [predictionRaceName, setPredictionRaceName] = useState(navPrediction?.race_name || null);
  // Engine that produced the prediction on screen ('v2' | 'v3' | 'live'), as
  // recorded when it was saved. Null for predictions saved before it was
  // recorded, which are shown without a version rather than guessed at.
  const [predictionEngine, setPredictionEngine] = useState(navPrediction?.summary?.prediction_engine || null);
  // Sections of the course the reference run has no comparable terrain for, and
  // that no secondary file covered either. Already excludes borrowed sections —
  // those have an answer, so there is nothing to warn about.
  const [terrainGaps, setTerrainGaps] = useState(navPrediction?.terrain_coverage_gaps || []);

  // Keep topbar session context in sync with current workout
  useEffect(() => {
    setActiveSessionData(activeWorkoutData || null);
    return () => setActiveSessionData(null);
  }, [activeWorkoutData, setActiveSessionData]);

  const [reanalyzeLoading, setReanalyzeLoading] = useState(false);
  const [error, setError] = useState('');
  const [accuracyInfoAnchor, setAccuracyInfoAnchor] = useState(null);
  const [paceRatioInfoAnchor, setPaceRatioInfoAnchor] = useState(null);
  const [stopsInfoAnchor, setStopsInfoAnchor] = useState(null);
  const [snapInfoAnchor, setSnapInfoAnchor] = useState(null);
  const [gpsInfoAnchor, setGpsInfoAnchor] = useState(null);

  // Re-analyze option checkboxes
  const [snapToGpx, setSnapToGpx] = useState(
    location.state?.analysisParams?.snap_to_gpx ||
    location.state?.workoutData?.parameters?.snap_to_gpx ||
    (location.state?.workoutData?.snap_to_gpx_result && !location.state?.workoutData?.snap_to_gpx_result?.skipped) ||
    false
  );
  const [applyGpsCorrections, setApplyGpsCorrections] = useState(
    location.state?.analysisParams?.apply_gps_corrections !== undefined
      ? location.state.analysisParams.apply_gps_corrections
      : location.state?.workoutData?.parameters?.apply_gps_corrections !== undefined
      ? location.state.workoutData.parameters.apply_gps_corrections
      : true
  );

  // Comparison drawer + share dialog
  const [compareDrawerOpen, setCompareDrawerOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // ── Race prediction state ─────────────────────────────────────────────────
  const [raceGpxFile, setRaceGpxFile] = useState(null);
  const [selectedRace, setSelectedRace] = useState(null);
  const [courseFitFileId, setCourseFitFileId] = useState(null);
  const [courseFitFile, setCourseFitFile] = useState(null);
  const [courseFitFilename, setCourseFitFilename] = useState(null);
  const [predictionCourseFitFileId, setPredictionCourseFitFileId] = useState(navPrediction?.course_fit_file_id || null);
  const [predictionCourseFitFile, setPredictionCourseFitFile] = useState(null);
  const [headerRaceDetailOpen, setHeaderRaceDetailOpen] = useState(false);
  const [racePickerOpen, setRacePickerOpen] = useState(false);
  const [predictLoading, setPredictLoading] = useState(false);
  const [predictionError, setPredictionError] = useState('');
  // Set when a course is picked, to run the prediction without a second click.
  // It cannot call handlePredict() inline: that reads the course out of state, and
  // the setters that just chose it have not been applied yet at that point. Going
  // through a render means the effect below sees the selection it is meant to use.
  const [autoPredict, setAutoPredict] = useState(false);

  // Deep prediction: uses the user's secondary reference files to calibrate
  // coefficients. Only offered when the user has at least one configured.
  const [deepPrediction, setDeepPrediction] = useState(!!navPrediction?.summary?.deep_prediction);
  const [secondaryRefCount, setSecondaryRefCount] = useState(0);

  // The primary prediction engine (v2 vs old 'live') is configured server-side via
  // the RUNNING_PREDICTION_ENGINE env var — no longer a UI toggle. The backend runs
  // v2 by default and falls back to live automatically when v2 inputs are missing.
  const [parallelV2, setParallelV2] = useState(null);
  const [liveFinishS, setLiveFinishS] = useState(null);

  // Grade-a-prediction (accuracy report) dialog
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  // Deep-prediction state that produced the currently shown result — lets a
  // checkbox toggle alone enable Rerun.
  const [appliedDeepPrediction, setAppliedDeepPrediction] = useState(!!navPrediction?.summary?.deep_prediction);

  // Pace ratio + stop time state — stored after prediction runs
  const _navFp = navPrediction?.performance_fingerprint;
  const _navRatio = _navFp?.pace_ratio ?? _navFp?.gap_ratio ?? null;
  const _navStopHHMM = navPrediction ? secsToHHMM(
    (navPrediction.summary?.predicted_total_checkpoint_stop_time_s || 0) +
    (navPrediction.summary?.predicted_total_free_stop_time_s || 0)
  ) : '';
  const [paceRatio, setPaceRatio] = useState(_navRatio != null ? String(Number(_navRatio).toFixed(3)) : '');
  const [computedPaceRatio, setComputedPaceRatio] = useState(_navRatio);
  // Finish time: what the user actually cares about, and what they edit. The
  // effort ratio above is the mechanism underneath — the backend solves for the
  // ratio that lands on this time, so nobody has to reason in multipliers.
  const _navTotalHHMM = navPrediction
    ? secsToHHMM(navPrediction.summary?.predicted_total_time_s || 0) : '';
  const [totalTime, setTotalTime] = useState(_navTotalHHMM);
  const [autoTotalTime, setAutoTotalTime] = useState(_navTotalHHMM || null);
  // Set when the finish time asked for was outside what this reference can
  // support, so the result shown is the closest reachable one, not the request.
  const [targetMissed, setTargetMissed] = useState(false);
  // Stop time: shown/edited as HH:MM total; backend scales all pauses uniformly
  const [stopTime, setStopTime] = useState(_navStopHHMM);
  const [autoStopTime, setAutoStopTime] = useState(_navStopHHMM || null);
  const [predictionFitFileId, setPredictionFitFileId] = useState(navPrediction?.fit_file_id || null);
  const [predictionRaceId, setPredictionRaceId] = useState(navPrediction?.race_id || null);
  const [predictionRaceGpxBlob, setPredictionRaceGpxBlob] = useState(null);
  // prediction_session_id returned by the backend — needed for "Share to club"
  const [predictionSessionId, setPredictionSessionId] = useState(navPrediction?.prediction_session_id || null);
  // Live tracking link already attached to this prediction (if any)
  const [liveTrackingLinkId, setLiveTrackingLinkId] = useState(navPrediction?.live_tracking_link_id || null);

  // Share prediction to club dialog
  const [shareToClubOpen, setShareToClubOpen] = useState(false);
  const [myClubs, setMyClubs] = useState([]);
  const [myClubsLoading, setMyClubsLoading] = useState(false);
  const [shareToClubSnack, setShareToClubSnack] = useState('');

  // Sync checkboxes when workoutData changes (e.g. after reanalyze)
  useEffect(() => {
    if (workoutData?.parameters?.apply_gps_corrections !== undefined) {
      setApplyGpsCorrections(workoutData.parameters.apply_gps_corrections);
    }
    if (workoutData?.snap_to_gpx_result?.skipped) {
      setSnapToGpx(false);
    }
  }, [workoutData]);

  // Auto-open comparison drawer when navigating from a club Compare button
  useEffect(() => {
    if (location.state?.autoOpenCompare && workoutData) {
      setCompareDrawerOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch how many secondary reference files the user has (for deep prediction)
  useEffect(() => {
    getRunningProfile()
      .then(data => setSecondaryRefCount((data?.secondary_fit_file_ids || []).length))
      .catch(() => { /* unauthenticated or no profile — hide the option */ });
  }, []);

  // A finished analysis is a page of charts, and nothing on it says a race
  // prediction is the next thing available. The control that starts one is a
  // single outlined button among several, so it announces itself whenever an
  // analysis appears with no course chosen yet. It stops early the moment the
  // user opens the picker or a course arrives — once they are in the flow the
  // flashing has done its job.
  const [flashRaceBtn, setFlashRaceBtn] = useState(false);

  const analysisSessionId = workoutData?.analysis_session_id || null;
  const fitFileId = workoutData?.fit_file_id || null;
  const race = workoutData?.race || null;
  // Which race the prediction picker filters to. This dialog is opened from the
  // analysis of an actual race, so it is that race.
  const gradeRaceId = race?.id ?? null;

  // ── Reanalyze / back to actual ────────────────────────────────────────────
  const handleReanalyze = async () => {
    // If showing a prediction, just go back to actual data
    if (isPrediction) {
      setActiveWorkoutData(workoutData);
      setIsPrediction(false);
      setPredictionRaceName(null);
      setRaceGpxFile(null);
      setSelectedRace(null);
      setPaceRatio('');
      setComputedPaceRatio(null);
      setStopTime('');
      setAutoStopTime(null);
      setPredictionFitFileId(null);
      setPredictionRaceId(null);
      setPredictionRaceGpxBlob(null);
      setPredictionSessionId(null);
      return;
    }

    if (!file && !reusedFileId) return;
    try {
      setReanalyzeLoading(true);
      setError('');
      const params = {
        ...(analysisParams || {}),
        reused_file_id: reusedFileId,
        snap_to_gpx: snapToGpx,
        apply_gps_corrections: applyGpsCorrections,
      };
      const fileToUpload = reusedFileId ? null : file;
      const response = await uploadRunningFit(fileToUpload, params);
      setActiveWorkoutData(response);
    } catch (err) {
      console.error('Reanalyze error:', err);
      setError(err.response?.data?.detail || 'Reanalysis failed');
    } finally {
      setReanalyzeLoading(false);
    }
  };

  const handleStartComparison = (source, snapToGpxB, applyGpsCorrectionsB) => {
    if (!source) return;
    setCompareDrawerOpen(false);

    if (source.type === 'pastComparison') {
      navigate('/running/compare', {
        state: {
          comparisonId: source.comparisonId,
          workoutA: workoutData,
          labelA: file?.name || null,
        },
      });
      return;
    }

    // A comparison needs a saved analysis session for A. Predictions and some
    // reused/shared views have none — bail with a clear message instead of navigating
    // to /running/compare, which silently bounces back to the dashboard.
    if (!analysisSessionId) {
      setShareToClubSnack(t('cannotCompareNoSession', 'This workout must be analyzed and saved before you can compare it.'));
      return;
    }

    navigate('/running/compare', {
      state: {
        baseSessionId: analysisSessionId,
        source,
        snapBToGpx: snapToGpxB,
        applyGpsCorrectionsBTo: applyGpsCorrectionsB,
        workoutA: workoutData,
        labelA: file?.name || null,
      },
    });
  };

  // ── Race / GPX selection ──────────────────────────────────────────────────
  const clearCourseState = () => {
    setRaceGpxFile(null);
    setSelectedRace(null);
    setCourseFitFileId(null);
    setCourseFitFile(null);
    setCourseFitFilename(null);
  };

  const handleRaceGpxSelect = async (source) => {
    setRacePickerOpen(false);
    setPredictionError('');
    if (source.type === 'gpxUpload') {
      clearCourseState();
      setRaceGpxFile(source.file);
      setAutoPredict(true);
    } else if (source.type === 'race') {
      clearCourseState();
      setSelectedRace(source.record);
      setAutoPredict(true);
    } else if (source.type === 'myfile') {
      clearCourseState();
      setCourseFitFileId(source.fileId);
      setCourseFitFilename(source.record?.filename || 'My FIT File');
      setAutoPredict(true);
    } else if (source.type === 'clubFile') {
      clearCourseState();
      try {
        const blob = await getClubFileBlob(source.clubId, source.clubFileId);
        setCourseFitFile(blob);
        setCourseFitFilename(source.filename || 'Club FIT File');
        // Only after the download worked — there is no course to predict against
        // if it did not, and the error below is what the user needs to see.
        setAutoPredict(true);
      } catch (err) {
        setPredictionError('Failed to download club file.');
      }
    } else if (source.type === 'prediction') {
      // Load a past prediction directly — no re-computation needed
      setPredictLoading(true);
      try {
        const data = await getRunningPrediction(source.predictionId);
        const label = data.race_name || source.record?.race_name || 'Past Prediction';
        setActiveWorkoutData(buildPredictionWorkoutData(data, data.race || null, data.race?.race_start || source.record?.race_start));
        setIsPrediction(true);
        setPredictionRaceName(label);
        // Store pace ratio + stop time from fingerprint / summary
        const fp = data.performance_fingerprint;
        const ratio = fp?.pace_ratio ?? fp?.gap_ratio ?? null;
        setComputedPaceRatio(ratio);
        setPaceRatio(ratio != null ? String(Number(ratio).toFixed(3)) : '');
        const totalHHMM = secsToHHMM(
          (data.summary?.predicted_total_checkpoint_stop_time_s || 0) +
          (data.summary?.predicted_total_free_stop_time_s || 0)
        );
        setAutoStopTime(totalHHMM);
        setStopTime(totalHHMM);
        const finishHHMM = secsToHHMM(data.summary?.predicted_total_time_s || 0);
        setAutoTotalTime(finishHHMM);
        setTotalTime(finishHHMM);
        setPredictionFitFileId(data.fit_file_id || null);
        setPredictionRaceId(data.race_id || null);
        setPredictionRaceGpxBlob(null);
        setPredictionSessionId(data.prediction_session_id || null);
        setLiveTrackingLinkId(data.live_tracking_link_id || null);
        setDeepPrediction(!!data.summary?.deep_prediction);
        setAppliedDeepPrediction(!!data.summary?.deep_prediction);
        setPredictionEngine(data.summary?.prediction_engine || null);
        setTerrainGaps(data.terrain_coverage_gaps || []);
      } catch (err) {
        setPredictionError(err?.response?.data?.detail || 'Failed to load prediction');
      } finally {
        setPredictLoading(false);
      }
    }
  };

  // ── Prediction ────────────────────────────────────────────────────────────
  const handlePredict = async () => {
    if (!fitFileId && !file) {
      setPredictionError('No reference FIT file available.');
      return;
    }
    const hasCourse = !!(raceGpxFile || selectedRace?.has_gpx || courseFitFileId || courseFitFile);
    if (!hasCourse) {
      setPredictionError('Please select a race, upload a GPX, or pick a FIT file as course.');
      return;
    }

    setPredictionError('');
    setPredictLoading(true);

    try {
      let gpxBlob = raceGpxFile;
      if (!gpxBlob && selectedRace?.has_gpx) {
        gpxBlob = await downloadRunningRaceGpx(selectedRace.id);
      }

      const data = await predictRunningRace({
        fitFileId: fitFileId || null,
        referenceFit: fitFileId ? null : file,
        raceGpx: gpxBlob,
        raceId: selectedRace?.id || null,
        courseFitFileId: courseFitFileId || null,
        courseFitFile: courseFitFile || null,
        courseFitFilename: courseFitFilename || null,
        deepPrediction: deepPrediction && secondaryRefCount > 0,
      });

      setParallelV2(data.parallel_v2 || null);
      setLiveFinishS(data.summary?.predicted_total_time_s ?? null);
      console.log('[FIT-course] predict response: checkpoints=', data.checkpoints, 'count=', data.checkpoints?.length);
      const label = selectedRace?.name || raceGpxFile?.name || courseFitFilename || 'Race';
      setActiveWorkoutData(buildPredictionWorkoutData(data, selectedRace, selectedRace?.race_start));
      setIsPrediction(true);
      setPredictionRaceName(label);
      // Store fingerprint data + stop time for rerun
      const fp = data.performance_fingerprint;
      const ratio = fp?.pace_ratio ?? fp?.gap_ratio ?? null;
      setComputedPaceRatio(ratio);
      setPaceRatio(ratio != null ? String(Number(ratio).toFixed(3)) : '');
      const totalHHMM = secsToHHMM(
        (data.summary?.predicted_total_checkpoint_stop_time_s || 0) +
        (data.summary?.predicted_total_free_stop_time_s || 0)
      );
      setAutoStopTime(totalHHMM);
      setStopTime(totalHHMM);
      const finishHHMM = secsToHHMM(data.summary?.predicted_total_time_s || 0);
      setAutoTotalTime(finishHHMM);
      setTotalTime(finishHHMM);
      setPredictionFitFileId(data.fit_file_id || fitFileId || null);
      setPredictionRaceId(data.race_id || selectedRace?.id || null);
      setPredictionRaceGpxBlob(gpxBlob);
      setPredictionCourseFitFileId(courseFitFileId || null);
      setPredictionCourseFitFile(courseFitFile || null);
      setPredictionSessionId(data.prediction_session_id || null);
      setLiveTrackingLinkId(data.live_tracking_link_id || null);
      setAppliedDeepPrediction(!!data.summary?.deep_prediction);
      setPredictionEngine(data.summary?.prediction_engine || null);
      setTerrainGaps(data.terrain_coverage_gaps || []);
    } catch (err) {
      setPredictionError(err?.response?.data?.detail || err?.message || 'Prediction failed');
    } finally {
      setPredictLoading(false);
    }
  };

  // Picking a course is itself the request to predict — pressing Predict afterwards
  // was always the next click, so it runs on selection instead. Deliberately keyed
  // only on the flag: it is set in the same batch as the course, so this render
  // already has the new selection, and re-running whenever a course field changes
  // would fire again on every later tweak.
  useEffect(() => {
    if (!autoPredict) return;
    setAutoPredict(false);
    handlePredict();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPredict]);

  // ── Rerun prediction with custom pace ratio ───────────────────────────────
  const handleRerun = async () => {
    const overrideFitFileId = predictionFitFileId || fitFileId;
    // Multi-file (democratic) predictions have no single reference FIT — they rerun
    // through the democratic endpoint using the same course + marked files.
    if (isMultiFilePrediction) {
      if (!predictionRaceId && !predictionCourseFitFileId) {
        setPredictionError('Cannot rerun this multi-file prediction (missing course).');
        return;
      }
    } else if (!overrideFitFileId && !file) {
      setPredictionError('No reference FIT file available for rerun.');
      return;
    }
    setPredictionError('');
    setPredictLoading(true);
    try {
      const parsedPaceRatio = Number(paceRatio);
      const paceModified = paceRatio !== '' && computedPaceRatio != null
        && Math.abs(parsedPaceRatio - Number(computedPaceRatio)) > 0.0001;
      const stopModified = isValidHHMM(stopTime) && stopTime !== autoStopTime;
      // Sent as an absolute total, not as a ratio against the figure on screen.
      // That figure has the per-checkpoint minimums baked into it while the ratio
      // scaled the raw budget underneath them, so on a race whose reference
      // barely stopped, asking for five times the 2 minutes shown changed nothing.
      const targetStopSeconds = stopModified ? hhmmToSecs(stopTime) : null;
      const totalModified = isValidHHMM(totalTime) && totalTime !== autoTotalTime;
      // The finish time wins over the ratio: it is what the user edited, and the
      // backend derives the ratio from it.
      const targetTotalSeconds = totalModified ? hhmmToSecs(totalTime) : null;
      setTargetMissed(false);
      let data;
      if (isMultiFilePrediction) {
        data = await predictDemocratic({
          raceId: predictionRaceId || null,
          courseFitFileId: predictionCourseFitFileId || null,
          paceRatio: paceModified ? parsedPaceRatio : null,
          targetStopTimeS: targetStopSeconds,
          targetTotalTimeS: targetTotalSeconds,
        });
      } else {
        const opts = {};
        if (paceModified) opts.pace_ratio = parsedPaceRatio;
        if (targetStopSeconds != null) opts.target_stop_time_s = targetStopSeconds;
        if (targetTotalSeconds != null) opts.target_total_time_s = targetTotalSeconds;
        data = await predictRunningRace({
          fitFileId: overrideFitFileId || null,
          referenceFit: overrideFitFileId ? null : file,
          raceGpx: predictionRaceGpxBlob,
          raceId: predictionRaceId,
          courseFitFileId: predictionCourseFitFileId || null,
          courseFitFile: predictionCourseFitFile || null,
          courseFitFilename: courseFitFilename || null,
          options: opts,
          deepPrediction: deepPrediction && secondaryRefCount > 0,
        });
      }
      setParallelV2(data.parallel_v2 || null);
      setLiveFinishS(data.summary?.predicted_total_time_s ?? null);
      // Carry the race forward so checkpoint service columns keep rendering — a
      // multi-file rerun (from the dashboard) has no selectedRace.
      const rerunRace = data.race || selectedRace || activeWorkoutData?.race || navPrediction?.race || null;
      setActiveWorkoutData(buildPredictionWorkoutData(data, rerunRace, rerunRace?.race_start));
      const fp = data.performance_fingerprint;
      setComputedPaceRatio(fp?.computed_pace_ratio ?? fp?.pace_ratio ?? fp?.gap_ratio ?? null);
      const newTotal = secsToHHMM(
        (data.summary?.predicted_total_checkpoint_stop_time_s || 0) +
        (data.summary?.predicted_total_free_stop_time_s || 0)
      );
      setStopTime(newTotal);
      // If stop was not overridden, the result IS the new auto baseline
      if (!stopModified) setAutoStopTime(newTotal);
      // The finish time always reflects what came back. When the user asked for
      // a specific one the solver should have matched it; when they did not,
      // this becomes the new baseline to adjust from.
      const newFinish = secsToHHMM(data.summary?.predicted_total_time_s || 0);
      setTotalTime(newFinish);
      if (!totalModified) setAutoTotalTime(newFinish);
      setTargetMissed(
        data.summary?.target_total_time_s != null && data.summary?.target_total_reached === false
      );
      setPredictionFitFileId(data.fit_file_id || overrideFitFileId || null);
      setPredictionRaceId(data.race_id || predictionRaceId || null);
      setPredictionSessionId(data.prediction_session_id || null);
      setLiveTrackingLinkId(data.live_tracking_link_id || null);
      setAppliedDeepPrediction(!!data.summary?.deep_prediction);
      setPredictionEngine(data.summary?.prediction_engine || null);
      setTerrainGaps(data.terrain_coverage_gaps || []);
    } catch (err) {
      setPredictionError(err?.response?.data?.detail || err?.message || 'Rerun failed');
    } finally {
      setPredictLoading(false);
    }
  };

  // ── Grade a past prediction against this actual race ──────────────────────
  // The picker fetches and filters its own tabs, so this only has to open it.
  const openGradeDialog = () => {
    if (!analysisSessionId) return;
    setGradeDialogOpen(true);
  };

  const handleGradePrediction = async (predictionSessionId) => {
    // Close first: the report can take a moment, and the picker that used to
    // host its own spinner is gone, so leaving it open looks like a dead click.
    setGradeDialogOpen(false);
    setShareToClubSnack(t('buildingReport', 'Building report…'));
    try {
      const rep = await createAccuracyReport(analysisSessionId, predictionSessionId);
      navigate(`/running/prediction-accuracy/${rep.id}`);
    } catch (err) {
      setShareToClubSnack(apiErrorMessage(err, 'Failed to build report'));
    }
  };

  // Picking a prediction from the "check against this race" dialog: normal users get a head-to-head
  // COMPARISON against that prediction (one consistent way to review a run vs its plan); the
  // distinct prediction-accuracy report stays admin-only, as a backup / engine-validation view.
  const handlePickCandidate = (predictionSessionId) => {
    if (isAdmin) {
      handleGradePrediction(predictionSessionId);
    } else {
      setGradeDialogOpen(false);
      handleStartComparison({ type: 'prediction', predictionId: predictionSessionId }, false, false);
    }
  };

  // ── Share prediction to club ──────────────────────────────────────────────
  const handleOpenShareToClub = async () => {
    setShareToClubOpen(true);
    if (myClubs.length === 0) {
      setMyClubsLoading(true);
      try {
        const clubs = await getMyClubs();
        setMyClubs(clubs);
      } catch {
        setMyClubs([]);
      } finally {
        setMyClubsLoading(false);
      }
    }
  };

  const handleShareToClub = async (clubId) => {
    if (!predictionSessionId) return;
    setShareToClubOpen(false);
    try {
      await shareClubPrediction(clubId, predictionSessionId);
      const club = myClubs.find((c) => c.id === clubId);
      setShareToClubSnack(`Prediction shared to ${club?.name || 'club'}`);
    } catch (err) {
      setShareToClubSnack(err?.response?.data?.detail || 'Failed to share prediction');
    }
  };

  const gpxLabel = selectedRace?.name || raceGpxFile?.name || courseFitFilename || null;
  const canPredict = !!(fitFileId || file) && !!(raceGpxFile || selectedRace?.has_gpx || courseFitFileId || courseFitFile);

  // Declared after gpxLabel because the dependency array is read during render,
  // not in the callback — referencing it earlier would hit the TDZ.
  // Keyed on workoutData, not analysisSessionId: that id only exists for signed-in
  // users, and an anonymous visitor trying the sample run is exactly who this is for.
  useEffect(() => {
    if (isPrediction || gpxLabel || !workoutData) { setFlashRaceBtn(false); return; }
    setFlashRaceBtn(true);
    const id = setTimeout(() => setFlashRaceBtn(false), PULSE_TOTAL_MS);
    return () => clearTimeout(id);
  }, [isPrediction, gpxLabel, workoutData]);

  // ── Early return — no data ────────────────────────────────────────────────
  if (!workoutData && !navPrediction) {
    return (
      <Box m={isMobile ? '10px' : '20px'}>
        <Alert severity="warning">
          {error || 'No workout data available. Please go to the dashboard and upload a FIT file.'}
        </Alert>
        <Box mt={2}>
          <Typography
            variant="h6"
            onClick={() => navigate('/running')}
            sx={{ cursor: 'pointer', color: colors.blueAccent[500] }}
          >
            ← Back to Running Dashboard
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Header slots ──────────────────────────────────────────────────────────
  // Race object backing the header info icon — for predictions this must come
  // from activeWorkoutData.race (populated on both fresh runs and reloads from
  // history), since `race` (workoutData.race) only applies to actual workouts.
  const headerRace = isPrediction ? activeWorkoutData?.race : race;

  const activityDateStr = (() => {
    const iso = activeWorkoutData?.chunks?.[0]?.data_start;
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  })();

  const headerLeft = (
    <Box>
      {/* Club breadcrumb — shown when analysis was opened from a club file */}
      {clubContext && (
        <Chip
          icon={<GroupIcon sx={{ fontSize: 14 }} />}
          label={clubContext.clubName}
          size="small"
          onClick={() => navigate(`/clubs/${clubContext.clubId}`)}
          sx={{
            mb: 0.75,
            cursor: 'pointer',
            backgroundColor: `${colors.blueAccent[800]}`,
            color: colors.blueAccent[300],
            fontSize: '0.72rem',
            height: 22,
            '& .MuiChip-icon': { color: colors.blueAccent[400] },
            '&:hover': { backgroundColor: colors.blueAccent[700] },
          }}
        />
      )}
      <Typography
        variant={isMobile ? 'h4' : 'h2'}
        fontWeight="bold"
        color={colors.grey[100]}
        mb={0.5}
      >
        {isPrediction ? t('racePrediction', 'Race Prediction') : t('runningAnalysis', 'Running Analysis')}
        {isPrediction && predictionEngine && (
          /* Deliberately tiny and superscripted: which engine answered is useful
             while the engines are being compared, but it is not part of the
             title and must not compete with the race name below it. */
          <Box
            component="span"
            sx={{
              ml: 0.75,
              fontSize: '0.5em',
              fontWeight: 600,
              letterSpacing: '0.04em',
              verticalAlign: 'super',
              color: colors.grey[400],
            }}
          >
            {predictionEngine}
          </Box>
        )}
      </Typography>
      <Typography
        variant={isMobile ? 'body1' : 'h5'}
        color={colors.grey[300]}
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'nowrap' : 'normal' }}
      >
        {isPrediction
          ? predictionRaceName
          : [
              activityDateStr,
              clubContext?.contributorEmail ? `by ${clubContext.contributorEmail}` : null,
              clubContext?.filename || activeWorkoutData?.fit_filename || file?.name,
            ].filter(Boolean).join(' · ')
        }
      </Typography>
      {/* Garmin brand guidelines: the device credit sits directly beneath the
          primary title, above the fold, on every view of this data. */}
      <GarminAttribution
        source={activeWorkoutData?.file_source}
        deviceName={activeWorkoutData?.device_name}
      />
      {isPrediction && (file?.name || workoutData?.fit_filename || navPrediction?.fit_filename) && (
        <Box display="flex" gap={2} mt={0.25} flexWrap="wrap">
          <Typography variant="caption" color={colors.grey[500]}>
            Ref: <strong style={{ color: colors.grey[400] }}>{file?.name || workoutData?.fit_filename || navPrediction?.fit_filename}</strong>
          </Typography>
          {predictionRaceName && (
            <Typography variant="caption" color={colors.grey[500]} display="flex" alignItems="center" gap={0.25}>
              Race: <strong style={{ color: colors.grey[400] }}>{predictionRaceName}</strong>
              {headerRace && (
                <IconButton size="small" onClick={() => setHeaderRaceDetailOpen(true)} sx={{ color: colors.blueAccent[400], p: 0.25 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Typography>
          )}
        </Box>
      )}
      {!isPrediction && race?.name && (
        <Typography variant="body2" color={colors.grey[500]} sx={{ mt: 0.25 }} display="flex" alignItems="center" gap={0.25}>
          Race: <strong style={{ color: colors.grey[400] }}>{race.name}</strong>
          <IconButton size="small" onClick={() => setHeaderRaceDetailOpen(true)} sx={{ color: colors.blueAccent[400], p: 0.25 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Typography>
      )}
      {headerRace && (
        <RaceDetailDialog
          race={headerRace}
          open={headerRaceDetailOpen}
          onClose={() => setHeaderRaceDetailOpen(false)}
        />
      )}
      {!isPrediction && activeWorkoutData?.file_description && (
        <Typography variant="body2" color={colors.grey[500]} sx={{ mt: 0.25 }}>
          {activeWorkoutData.file_description}
        </Typography>
      )}
    </Box>
  );

  const headerRight = (
    <>
      {/* Reanalyze / Back to actual */}
      {(file || reusedFileId || analysisSessionId) && (
        <>
          {!isPrediction && race && race.gpx_data && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={snapToGpx}
                  onChange={(e) => setSnapToGpx(e.target.checked)}
                  size="small"
                  sx={{ color: colors.blueAccent[400], '&.Mui-checked': { color: colors.blueAccent[400] } }}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Typography variant="body2" color={colors.grey[300]}>{t('snapToGpx', 'Snap to GPX')}</Typography>
                  <IconButton size="small" onClick={(e) => { e.preventDefault(); setSnapInfoAnchor(e.currentTarget); }} sx={{ color: colors.grey[500], p: 0, mb: 0.75 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                  <Popover open={Boolean(snapInfoAnchor)} anchorEl={snapInfoAnchor} onClose={() => setSnapInfoAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                    <Box sx={{ p: 2, maxWidth: 300 }}>
                      <Typography variant="subtitle2" gutterBottom>{t('snapToGpxInfoTitle', 'Snap to GPX')}</Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('snapToGpxInfo1', 'Aligns your GPS track to the race course line. Useful when your watch drifted off the route — e.g. on narrow trails or tunnels — and you want distances and elevation to match the official course.')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('snapToGpxInfo2', 'Also trims any GPS recording your watch continued after you crossed the finish line.')}
                      </Typography>
                    </Box>
                  </Popover>
                </Box>
              }
              sx={{ color: colors.grey[300], mr: 0 }}
            />
          )}
          {!isPrediction && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyGpsCorrections}
                  onChange={(e) => setApplyGpsCorrections(e.target.checked)}
                  disabled={
                    workoutData?.sanitization &&
                    !workoutData.sanitization.applied &&
                    !workoutData.sanitization.user_disabled
                  }
                  size="small"
                  sx={{
                    color: colors.blueAccent[400],
                    '&.Mui-checked': { color: colors.blueAccent[400] },
                    '&.Mui-disabled': { color: colors.grey[600] },
                  }}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Typography variant="body2" color={colors.grey[300]}>{t('applyGpsCorrections', 'GPS Corrections')}</Typography>
                  <IconButton size="small" onClick={(e) => { e.preventDefault(); setGpsInfoAnchor(e.currentTarget); }} sx={{ color: colors.grey[500], p: 0, mb: 0.75 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                  <Popover open={Boolean(gpsInfoAnchor)} anchorEl={gpsInfoAnchor} onClose={() => setGpsInfoAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                    <Box sx={{ p: 2, maxWidth: 300 }}>
                      <Typography variant="subtitle2" gutterBottom>{t('gpsCorrectionsInfoTitle', 'GPS Corrections')}</Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('gpsCorrectionsInfo1', 'Detects and removes GPS spikes — sudden jumps in position that your watch recorded but you never actually ran. These spikes inflate your total distance and can distort pace.')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('gpsCorrectionsInfo2', 'For example, a watch losing signal briefly in a valley and then snapping back can add 200–300m of fake distance. This option filters those out automatically.')}
                      </Typography>
                    </Box>
                  </Popover>
                </Box>
              }
              sx={{ color: colors.grey[300], mr: 0 }}
            />
          )}
          <Button
            variant="contained"
            onClick={handleReanalyze}
            disabled={reanalyzeLoading}
            startIcon={
              reanalyzeLoading ? <CircularProgress size={18} color="inherit" />
                : isPrediction ? <UndoIcon />
                : <RefreshIcon />
            }
            sx={{
              backgroundColor: isPrediction ? colors.primary[600] : colors.greenAccent[600],
              color: colors.grey[100],
              minWidth: isMobile ? 'auto' : undefined,
              px: isMobile ? 2 : undefined,
              fontSize: isMobile ? '0.75rem' : undefined,
              '&:hover': { backgroundColor: isPrediction ? colors.primary[500] : colors.greenAccent[700] },
              '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
            }}
          >
            {reanalyzeLoading ? t('reanalyzing', 'Reanalyzing...') : isPrediction ? t('backToActual', 'Back to actual') : t('reanalyze', 'Reanalyze')}
          </Button>
        </>
      )}

    </>
  );

  // Navigation / sharing actions. Kept out of headerRight so RunningAnalysisView can
  // give them a row of their own — on a phone they used to wrap into whatever gap the
  // checkboxes and Reanalyze left behind. The view appends its mkpaceLIVE shortcut to
  // this same row.
  // Split out of headerActions so the view can park it wherever it fits: on a phone
  // it goes up beside the Advanced control, which leaves the remaining actions on a
  // single line instead of wrapping.
  const headerNav = (
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/running')}
        sx={{
          color: colors.grey[100],
          borderColor: colors.primary[400],
          minWidth: isMobile ? 'auto' : undefined,
          px: isMobile ? 2 : undefined,
          fontSize: isMobile ? '0.75rem' : undefined,
          '&:hover': { borderColor: colors.primary[300], backgroundColor: colors.primary[700] },
        }}
      >
        {isMobile ? t('runningDashboard', 'Dashboard') : t('backToRunningDashboard', 'Back to Running Dashboard')}
      </Button>
  );

  const headerActions = (
    <>
      {/* Share prediction to club — only for predictions with a saved session */}
      {isPrediction && predictionSessionId && (
        <Button
          variant="outlined"
          startIcon={<ShareIcon />}
          onClick={handleOpenShareToClub}
          sx={{
            color: colors.greenAccent[300],
            borderColor: colors.greenAccent[600],
            minWidth: isMobile ? 'auto' : undefined,
            px: isMobile ? 2 : undefined,
            fontSize: isMobile ? '0.75rem' : undefined,
            textTransform: 'none',
            '&:hover': { borderColor: colors.greenAccent[400], backgroundColor: colors.greenAccent[900] },
          }}
        >
          {isMobile ? t('share', 'Share') : t('shareToClub', 'Share to Club')}
        </Button>
      )}

      {/* Compare / Share — only for actual analysis */}
      {!isPrediction && analysisSessionId && (
        <>
          <Button
            variant="outlined"
            startIcon={<CompareArrowsIcon />}
            onClick={() => setCompareDrawerOpen(true)}
            sx={{
              color: colors.blueAccent[300],
              borderColor: colors.blueAccent[600],
              minWidth: isMobile ? 'auto' : undefined,
              px: isMobile ? 2 : undefined,
              fontSize: isMobile ? '0.75rem' : undefined,
              '&:hover': { borderColor: colors.blueAccent[400], backgroundColor: colors.blueAccent[900] },
            }}
          >
            {isMobile ? t('compare', 'Compare') : t('compareWorkout', 'Compare Workout')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={() => setShareDialogOpen(true)}
            sx={{
              color: colors.greenAccent[300],
              borderColor: colors.greenAccent[600],
              minWidth: isMobile ? 'auto' : undefined,
              px: isMobile ? 2 : undefined,
              fontSize: isMobile ? '0.75rem' : undefined,
              '&:hover': { borderColor: colors.greenAccent[400], backgroundColor: colors.greenAccent[900] },
            }}
          >
            {t('share', 'Share')}
          </Button>
        </>
      )}

    </>
  );

  // Multi-file (democratic) race prediction: it already pools every secondary file,
  // so "Deep prediction" is meaningless here — detect it to hide that control.
  const isMultiFilePrediction = isPrediction && (
    (activeWorkoutData?.performance_fingerprint || navPrediction?.performance_fingerprint)?.mode === 'democratic'
    || (activeWorkoutData?.fit_filename || navPrediction?.fit_filename) === 'Multi-file race prediction'
  );

  const paceRatioIsModified = paceRatio !== '' && computedPaceRatio != null
    && Math.abs(Number(paceRatio) - Number(computedPaceRatio)) > 0.0001;
  const stopTimeIsModified = isValidHHMM(stopTime) && stopTime !== autoStopTime;
  const totalTimeIsModified = isValidHHMM(totalTime) && totalTime !== autoTotalTime;
  // Toggling deep prediction (when the user has secondary files) also warrants a rerun.
  const deepPredictionIsModified = secondaryRefCount > 0 && !isMultiFilePrediction
    && (deepPrediction && secondaryRefCount > 0) !== appliedDeepPrediction;
  const anyRatioModified = paceRatioIsModified || stopTimeIsModified
    || totalTimeIsModified || deepPredictionIsModified;

  const paceRatioBar = isPrediction && (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[800] }}>
      <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">

        {/* ── Finish time field ── */}
        {/* The effort ratio that produces this time is solved for on the server.
            Showing the ratio instead asked the user to reason in multipliers to
            express something they already know: the time they want to finish in. */}
        <Box display="flex" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="subtitle2" color={colors.grey[300]} sx={{ userSelect: 'none', whiteSpace: 'nowrap' }}>
              {t('finishTimeLabel', 'Finish time:')}
            </Typography>
            <IconButton size="small" onClick={(e) => setPaceRatioInfoAnchor(e.currentTarget)} sx={{ color: colors.grey[400], p: 0, mb: 1 }}>
              <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </IconButton>
            <Popover
              open={Boolean(paceRatioInfoAnchor)}
              anchorEl={paceRatioInfoAnchor}
              onClose={() => setPaceRatioInfoAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, maxWidth: 320 }}>
                <Typography variant="subtitle2" gutterBottom>{t('finishTimeInfoTitle', 'Finish time')}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('finishTimeInfo1', 'The time the prediction works out from your reference run. Change it to plan for a different finish and every split, pace and checkpoint time moves with it.')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('finishTimeInfo2', 'The shape of the race stays yours — the climbs stay slow and the descents stay fast in the same proportions. Only the overall level shifts.')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('finishTimeInfo3', 'Ask for something far beyond your reference and the closest reachable time is shown instead, with a note saying so.')}
                </Typography>
              </Box>
            </Popover>
          </Box>
          <Typography variant="body2" color={colors.grey[500]} sx={{ fontStyle: 'italic', whiteSpace: 'nowrap', minWidth: 72 }}>
            {autoTotalTime != null ? t('autoPaceRatio', 'auto: {{value}}', { value: autoTotalTime }) : ''}
          </Typography>
          <TextField
            size="small"
            value={totalTime}
            onChange={(e) => setTotalTime(e.target.value)}
            placeholder="h:mm"
            error={totalTime !== '' && !isValidHHMM(totalTime)}
            InputProps={totalTimeIsModified ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setTotalTime(autoTotalTime ?? '')} tabIndex={-1} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14, color: colors.grey[500] }} />
                  </IconButton>
                </InputAdornment>
              ),
            } : undefined}
            sx={{
              width: 110,
              '& .MuiOutlinedInput-root': {
                color: totalTimeIsModified ? colors.blueAccent[300] : colors.grey[200],
                '& fieldset': { borderColor: totalTimeIsModified ? colors.blueAccent[500] : colors.grey[600] },
                '&:hover fieldset': { borderColor: colors.blueAccent[400] },
              },
            }}
          />
        </Box>

        {/* ── Total stops field ── */}
        <Box display="flex" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="subtitle2" color={colors.grey[300]} sx={{ userSelect: 'none', whiteSpace: 'nowrap' }}>
              {t('totalStopsLabel', 'Total stops:')}
            </Typography>
            <IconButton size="small" onClick={(e) => setStopsInfoAnchor(e.currentTarget)} sx={{ color: colors.grey[400], p: 0, mb: 1 }}>
              <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            </IconButton>
            <Popover
              open={Boolean(stopsInfoAnchor)}
              anchorEl={stopsInfoAnchor}
              onClose={() => setStopsInfoAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, maxWidth: 320 }}>
                <Typography variant="subtitle2" gutterBottom>{t('totalStopsInfoTitle', 'Total Stops')}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('totalStopsInfo1', "The total time you expect to spend stopped at aid stations and checkpoints throughout the race — not running, just resting, eating, or getting your kit sorted.")}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('totalStopsInfo2', "The system estimates this automatically from the race's checkpoints. You can override it by typing a total in H:MM format.")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Examples:</strong> {t('totalStopsInfo3', "0:30 → 30 min total stops (fast racing style, minimal aid station time). 1:45 → 1h45m of stops (longer ultra, taking your time at checkpoints to eat and rest).")}
                </Typography>
              </Box>
            </Popover>
          </Box>
          <Typography variant="body2" color={colors.grey[500]} sx={{ fontStyle: 'italic', whiteSpace: 'nowrap', minWidth: 60 }}>
            {autoStopTime != null ? t('autoPaceRatio', 'auto: {{value}}', { value: autoStopTime }) : ''}
          </Typography>
          <TextField
            size="small"
            value={stopTime}
            onChange={(e) => setStopTime(e.target.value)}
            placeholder={t('hmmPlaceholder', 'H:MM')}
            inputProps={{ style: { fontVariantNumeric: 'tabular-nums' } }}
            InputProps={stopTimeIsModified ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setStopTime(autoStopTime ?? '')} tabIndex={-1} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14, color: colors.grey[500] }} />
                  </IconButton>
                </InputAdornment>
              ),
            } : undefined}
            sx={{
              width: 100,
              '& .MuiOutlinedInput-root': {
                color: stopTimeIsModified ? colors.blueAccent[300] : colors.grey[200],
                '& fieldset': { borderColor: stopTimeIsModified ? colors.blueAccent[500] : colors.grey[600] },
                '&:hover fieldset': { borderColor: colors.blueAccent[400] },
              },
            }}
          />
        </Box>

        {/* ── Deep prediction toggle + Rerun button on the right ── */}
        <Box display="flex" alignItems="center" gap={1.5} sx={{ ml: 'auto' }}>
          {predictionError && (
            <Alert severity="error" sx={{ py: 0 }}>{predictionError}</Alert>
          )}
          {secondaryRefCount > 0 && !isMultiFilePrediction && (
            <Tooltip
              title={t('deepPredictionHint', 'Uses your secondary reference files to personalise the fatigue and terrain coefficients. Takes longer to compute.')}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={deepPrediction}
                    onChange={(e) => setDeepPrediction(e.target.checked)}
                    sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.greenAccent[400] } }}
                  />
                }
                label={
                  <Typography variant="caption" color={colors.grey[200]}>
                    {t('deepPrediction', 'Deep prediction')}
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={predictLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={handleRerun}
            disabled={!anyRatioModified || predictLoading || (paceRatioIsModified && isNaN(Number(paceRatio))) || (stopTimeIsModified && !isValidHHMM(stopTime)) || (totalTime !== '' && !isValidHHMM(totalTime))}
            sx={{
              backgroundColor: anyRatioModified ? colors.blueAccent[700] : colors.grey[700],
              color: anyRatioModified ? colors.grey[100] : colors.grey[500],
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: colors.blueAccent[600] },
              '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
            }}
          >
            {predictLoading ? t('runningEllipsis', 'Running...') : t('rerun', 'Rerun')}
          </Button>
        </Box>

      </Box>

      {/* The finish time asked for needed more than the effort knob can reach.
          Shown in the same band as the other qualifications on the number, for
          the same reason the terrain note is. */}
      {targetMissed && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${colors.grey[700]}` }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: colors.grey[400] }} />
          <Typography variant="caption" color={colors.grey[300]}>
            {t('targetMissedBanner',
               'That finish time is further from your reference run than a prediction can honestly stretch. This is the closest it reaches.')}
          </Typography>
        </Box>
      )}

      {/* Terrain the reference cannot answer for. It lives in this band rather
          than in a banner of its own because it belongs with the other things
          that qualify the number — and a standalone MUI Alert brought its own
          light palette into a dark page. */}
      {terrainGaps.length > 0 && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${colors.grey[700]}` }}>
          <TerrainIcon sx={{ fontSize: 16, color: colors.grey[400] }} />
          <Typography variant="caption" color={colors.grey[300]}>
            {t('terrainGapBanner',
               'Some sections are steeper than anything in your reference run. Their pace is estimated, not matched.')}
          </Typography>
        </Box>
      )}
    </Paper>
  );

  /**
   * Race-prediction bar.
   *
   * Laid out as explicit lines on mobile rather than left to flex-wrap, which used to
   * scatter six controls across two and a half ragged rows. Three things went:
   *  - the "i" popover beside the "?" — it paraphrased the manual that RacePredictionHelp
   *    already opens, so it was a second copy of the same text to keep in sync;
   *  - the "Change" button — the chip names the race, so the chip itself reopens the
   *    picker and "✕" clears it. One control instead of two;
   *  - "Prediction accuracy" once a race is chosen (it grades a PAST prediction, a
   *    different job from the one this bar is now set up for).
   */
  const predictionBar = !isPrediction && (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[800] }}>
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        gap={isMobile ? 1 : 1.5}
        flexWrap={isMobile ? 'nowrap' : 'wrap'}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="subtitle2" color={colors.grey[300]}>
            {t('racePrediction', 'Race Prediction')}
          </Typography>
          <RacePredictionHelp fontSize={15} color={colors.grey[400]} />
        </Box>

        {/* Course + the action it enables, on one line. */}
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ flexGrow: 1 }}>
          {gpxLabel ? (
            <Tooltip title={t('changeRace', 'Change')}>
              <Chip
                label={gpxLabel}
                size="small"
                color="primary"
                onClick={() => setRacePickerOpen(true)}
                onDelete={() => { clearCourseState(); setPredictionError(''); }}
              />
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RouteIcon />}
              onClick={() => { setFlashRaceBtn(false); setRacePickerOpen(true); }}
              sx={{
                color: colors.grey[200],
                borderColor: colors.grey[600],
                textTransform: 'none',
                '&:hover': { borderColor: colors.blueAccent[400] },
                ...(flashRaceBtn ? {
                  '@keyframes raceBtnFlash': {
                    '0%, 100%': {
                      borderColor: colors.grey[600],
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                    },
                    '50%': {
                      borderColor: pulse.edge,
                      backgroundColor: pulse.fill,
                      boxShadow: `0 0 0 4px ${pulse.glow}`,
                    },
                  },
                  animation: pulseAnimation('raceBtnFlash'),
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    borderColor: pulse.edge,
                    boxShadow: `0 0 0 3px ${pulse.glow}`,
                  },
                } : {}),
              }}
            >
              {isMobile ? t('selectRace', 'Select Race') : t('selectRaceGpx', 'Select Race / GPX')}
            </Button>
          )}

          {gpxLabel && (
            <Button
              variant="contained"
              size="small"
              startIcon={predictLoading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              onClick={handlePredict}
              disabled={!canPredict || predictLoading}
              sx={{
                ml: 'auto',
                backgroundColor: colors.blueAccent[700],
                '&:hover': { backgroundColor: colors.blueAccent[600] },
                '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
              }}
            >
              {predictLoading ? t('predicting', 'Predicting...') : t('predict', 'Predict')}
            </Button>
          )}

          {analysisSessionId && !gpxLabel && (
            <Box display="flex" alignItems="center" gap={0.25} sx={{ ml: 'auto' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FactCheckIcon />}
                onClick={openGradeDialog}
                sx={{
                  color: colors.grey[200],
                  borderColor: colors.grey[600],
                  textTransform: 'none',
                  '&:hover': { borderColor: colors.greenAccent[400] },
                }}
              >
                {isAdmin ? t('predictionAccuracy', 'Prediction accuracy') : t('compareToPrediction', 'Compare to prediction')}
              </Button>
              <IconButton
                size="small"
                onClick={(e) => setAccuracyInfoAnchor(e.currentTarget)}
                sx={{ color: colors.grey[400], p: 0.25 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <Popover
                open={Boolean(accuracyInfoAnchor)}
                anchorEl={accuracyInfoAnchor}
                onClose={() => setAccuracyInfoAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 2, maxWidth: 320 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('predictionAccuracyInfoTitle', 'Prediction accuracy')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('predictionAccuracyInfo1', 'Compare a past prediction of this race against how the run actually went — checkpoint by checkpoint, climb by climb.')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('predictionAccuracyInfo2', 'It highlights where the prediction was too fast or too slow, so you can see how reliable it was and where it can improve.')}
                  </Typography>
                </Box>
              </Popover>
            </Box>
          )}
        </Box>

        {/* Only meaningful with a course chosen and secondary files to draw on. */}
        {gpxLabel && secondaryRefCount > 0 && (
          <Tooltip
            title={t('deepPredictionHint', 'Uses your secondary reference files to personalise the fatigue and terrain coefficients. Takes longer to compute.')}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={deepPrediction}
                  onChange={(e) => setDeepPrediction(e.target.checked)}
                  sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.greenAccent[400] } }}
                />
              }
              label={
                <Typography variant="caption" color={colors.grey[200]}>
                  {t('deepPrediction', 'Deep prediction')}
                </Typography>
              }
              sx={{ mr: 0, alignSelf: 'flex-start' }}
            />
          </Tooltip>
        )}
      </Box>

      {predictionError && (
        <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>{predictionError}</Alert>
      )}
    </Paper>
  );

  // Comparison panel — shown after a prediction whenever the v2 result is present.
  const parallelV2Panel = parallelV2 && (
    <Paper sx={{ p: 1.5, mb: 2, borderRadius: 1, backgroundColor: colors.primary[800], borderLeft: `3px solid ${colors.blueAccent[400]}` }}>
      <Typography variant="caption" color={colors.blueAccent[300]} fontWeight={600}>
        {t('v2Algorithm', 'v2 parallel algorithm')} · {parallelV2.mode}
        <Typography component="span" variant="caption" color={colors.grey[500]}> ({t('compareOnly', 'comparison only — live result unchanged')})</Typography>
      </Typography>
      <Box display="flex" gap={3} flexWrap="wrap" mt={0.5}>
        <Typography variant="caption">{t('liveFinish', 'Live finish')}: <b>{liveFinishS != null ? secsToHHMM(liveFinishS) : '—'}</b></Typography>
        <Typography variant="caption" color={colors.blueAccent[200]}>{t('v2Finish', 'v2 finish')}: <b>{secsToHHMM(parallelV2.predicted_total_time_s)}</b></Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          {t('moving', 'moving')} {secsToHHMM(parallelV2.predicted_moving_time_s)} · {t('stops', 'stops')} {secsToHHMM(parallelV2.predicted_stop_time_s)}
        </Typography>
        <Typography variant="caption" color={colors.grey[500]}>
          β={parallelV2.beta} · stop_law C={parallelV2.stop_law?.[0]}, p={parallelV2.stop_law?.[1]}
        </Typography>
      </Box>
      {parallelV2.reference_weights && Object.keys(parallelV2.reference_weights).length > 1 && (
        <Typography variant="caption" color={colors.grey[500]} display="block" sx={{ mt: 0.25 }}>
          {t('refs', 'refs')}: {Object.entries(parallelV2.reference_weights).map(([k, v]) => `${k}:${(v * 100).toFixed(0)}%`).join('  ')}
        </Typography>
      )}
    </Paper>
  );

  return (
    <>
      {/* ── Live tracking (predictions only, once a session is saved) ── */}
      {isPrediction && predictionSessionId && (
        <Box mb={2}>
          <LiveTrackingPanel
            predictionId={predictionSessionId}
            clubPredictionId={clubContext?.clubPredictionId || null}
            initialLinkId={liveTrackingLinkId}
          />
        </Box>
      )}

      {/* ── v2 parallel-algorithm comparison (opt-in) ── */}
      {parallelV2Panel}

      {/* ── Main analysis view (actual or predicted) ── */}
      <RunningAnalysisView
        workoutData={activeWorkoutData}
        headerLeft={headerLeft}
        headerRight={headerRight}
        headerActions={headerActions}
        headerNav={headerNav}
        belowHeader={predictionBar || paceRatioBar}
        buildRacePlanFn={
          isPrediction && predictionSessionId
            ? () => buildPredictionRacePlan(predictionSessionId)
            : analysisSessionId
            ? () => buildRacePlan(analysisSessionId)
            : null
        }
      />

      {/* ── Course / race picker drawer ── */}
      <CourseSelectorDrawer
        open={racePickerOpen}
        onClose={() => setRacePickerOpen(false)}
        onSelect={handleRaceGpxSelect}
        withPredictions
        predictionsFilter={fitFileId ? (p) => p.fit_file_id === fitFileId : null}
      />

      {/* ── Grade-a-prediction picker ──
          The same drawer the file pickers use, cut down to the two tabs that
          make sense here and filtered to this race. It replaces a bespoke dialog
          that listed predictions as text rows, where two runs of the same race
          differed only by a date buried in a subtitle. */}
      <WorkoutPickerDrawer
        open={gradeDialogOpen}
        onClose={() => setGradeDialogOpen(false)}
        onSelect={(source) => {
          if (source?.predictionId) handlePickCandidate(source.predictionId);
        }}
        // The race goes in the title, which is why the grid has no Race column.
        title={(isAdmin
          ? t('predictionAccuracyTitle', 'Check prediction accuracy against this race')
          : t('compareToPredictionTitle', 'Compare to your prediction for this race'))
          + (race?.name ? ` — ${race.name}` : '')}
        tabs={['predictions', 'clubPredictions']}
        defaultTab="predictions"
        actionLabel={isAdmin ? t('checkAccuracy', 'Check accuracy') : t('compare', 'Compare')}
        // Only this race. My Predictions narrows server-side, because that list
        // is capped and a client-side filter would sieve an already-truncated
        // page. Club predictions come back whole, so filtering them here is fine.
        predictionsRaceId={gradeRaceId}
        predictionsSingleRace
        clubPredictionsFilter={(row) => gradeRaceId != null && row.race_id === gradeRaceId}
      />

      <ComparisonSelectorDrawer
        open={compareDrawerOpen}
        onClose={() => setCompareDrawerOpen(false)}
        onStart={handleStartComparison}
        hasRaceGpx={!!(race && race.gpx_data)}
        baseSessionId={analysisSessionId}
        defaultSnapToGpx={snapToGpx}
        defaultApplyGpsCorrections={applyGpsCorrections}
        clubId={clubContext?.clubId || null}
      />

      {/* Share the workout — same dialog as the My Running Files list (club or community). */}
      <ShareWorkoutDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        file={fitFileId ? {
          id: fitFileId,
          filename: workoutData?.fit_filename || file?.name || 'this workout',
          race_name: race?.name || null,
          location_name: workoutData?.location_name || null,
          race_id: race?.id || workoutData?.race_id || null,
        } : null}
      />

      {/* ── Share prediction to club dialog ── */}
      <Dialog open={shareToClubOpen} onClose={() => setShareToClubOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('sharePredictionToClub', 'Share Prediction to Club')}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {myClubsLoading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : myClubs.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                {t('noClubsYet', "You're not a member of any club yet.")}
              </Typography>
            </Box>
          ) : (
            <List dense>
              {myClubs.map((club) => (
                <ListItemButton key={club.id} onClick={() => handleShareToClub(club.id)}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: club.color || colors.blueAccent[400],
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={club.name}
                    secondary={`${club.member_count} member${club.member_count !== 1 ? 's' : ''}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareToClubOpen(false)}>{t('cancel', 'Cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar for share-to-club feedback ── */}
      <Snackbar
        open={!!shareToClubSnack}
        autoHideDuration={3000}
        onClose={() => setShareToClubSnack('')}
        message={shareToClubSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default RunningAnalysis;
